<script setup lang="ts">
/**
 * The matrix's topics and where they sit.
 *
 * Two sliders per topic rather than two number fields. A score on a five point
 * scale is a judgement, not a measurement, and a slider says that: nobody
 * agonises over whether a topic is 3.4 or 3.6 when they are dragging it.
 */
import { useI18n } from 'vue-i18n'

import {
  newMaterialityEntry,
  type MaterialityEntry,
  type MaterialitySection,
} from '@/domain/section'
import InspectorField from '@/sections/InspectorField.vue'

const { t } = useI18n()

const props = defineProps<{ section: MaterialitySection }>()
const emit = defineEmits<{ change: [section: MaterialitySection] }>()

function set(changes: Partial<MaterialitySection>): void {
  emit('change', { ...props.section, ...changes })
}

function setEntry(id: string, changes: Partial<MaterialityEntry>): void {
  set({
    entries: props.section.entries.map((entry) =>
      entry.id === id ? { ...entry, ...changes } : entry,
    ),
  })
}

function add(): void {
  set({ entries: [...props.section.entries, newMaterialityEntry()] })
}

function remove(id: string): void {
  set({ entries: props.section.entries.filter((entry) => entry.id !== id) })
}

function number(event: Event): number {
  return Number((event.target as HTMLInputElement).value)
}
</script>

<template>
  <div class="fields">
    <div class="topics">
      <div v-for="entry in section.entries" :key="entry.id" class="topic">
        <div class="topic__head">
          <input
            class="b-input topic__name"
            :value="entry.topic"
            :placeholder="t('inspector.materiality.topicPlaceholder')"
            @input="setEntry(entry.id, { topic: ($event.target as HTMLInputElement).value })"
          />
          <button
            type="button"
            class="b-icon-btn b-btn--danger"
            :aria-label="t('inspector.materiality.removeTopic')"
            @click="remove(entry.id)"
          >
            <svg width="12" height="12" viewBox="0 0 16 16" aria-hidden="true">
              <path
                d="M4 4l8 8M12 4l-8 8"
                fill="none"
                stroke="currentColor"
                stroke-width="1.4"
                stroke-linecap="round"
              />
            </svg>
          </button>
        </div>

        <label class="slider">
          <span class="slider__label">{{ t('inspector.materiality.impact') }}</span>
          <input
            type="range"
            min="0"
            max="5"
            step="0.5"
            :value="entry.impact"
            @input="setEntry(entry.id, { impact: number($event) })"
          />
          <span class="slider__value">{{ entry.impact }}</span>
        </label>

        <label class="slider">
          <span class="slider__label">{{ t('inspector.materiality.financial') }}</span>
          <input
            type="range"
            min="0"
            max="5"
            step="0.5"
            :value="entry.financial"
            @input="setEntry(entry.id, { financial: number($event) })"
          />
          <span class="slider__value">{{ entry.financial }}</span>
        </label>
      </div>
    </div>

    <button type="button" class="b-btn b-btn--outline" @click="add">
      {{ t('inspector.materiality.addTopic') }}
    </button>

    <InspectorField
      :label="t('inspector.materiality.threshold')"
      :hint="t('inspector.materiality.thresholdHint')"
    >
      <label class="slider slider--wide">
        <input
          type="range"
          min="0"
          max="5"
          step="0.5"
          :value="section.threshold"
          @input="set({ threshold: number($event) })"
        />
        <span class="slider__value">{{ section.threshold }}</span>
      </label>
    </InspectorField>

    <InspectorField :label="t('inspector.materiality.caption')">
      <input
        class="b-input"
        :value="section.caption"
        @input="set({ caption: ($event.target as HTMLInputElement).value })"
      />
    </InspectorField>
  </div>
</template>

<style scoped>
.fields {
  display: flex;
  flex-direction: column;
  gap: var(--sp-6);
}

.topics {
  display: flex;
  flex-direction: column;
  gap: var(--sp-4);
}

.topic {
  display: flex;
  flex-direction: column;
  gap: var(--sp-3);
  padding: var(--sp-4);
  border-radius: var(--radius);
  background: var(--panel-raised);
  box-shadow: inset 0 0 0 1px var(--hairline);
}

.topic__head {
  display: flex;
  align-items: center;
  gap: var(--sp-3);
}

.topic__name {
  flex: 1;
  min-width: 0;
}

.slider {
  display: flex;
  align-items: center;
  gap: var(--sp-3);
}

.slider__label {
  width: 62px;
  flex: none;
  color: var(--ink-faint);
  font-size: var(--text-xs);
}

.slider input {
  flex: 1;
  min-width: 0;
  accent-color: var(--accent);
}

.slider__value {
  width: 26px;
  flex: none;
  color: var(--ink-soft);
  font-size: var(--text-xs);
  font-variant-numeric: tabular-nums;
  text-align: right;
}

.slider--wide .slider__label {
  display: none;
}
</style>
